import { useCallback, useMemo, useRef, useState } from 'react';

import {
  FocusContext,
  type FocusScopeRegistration,
} from '../contexts/FocusContext.js';
import { useFocusContext } from '../hooks/useFocusContext.js';
import type { WithChildren } from '../types/with-children.js';
import { getProperInertValue } from '../utils/get-inert-value.js';
import { mergeProps } from '../utils/merge-props.js';

/**
 * Provider that can be reused in components for storing the focus context props.
 * Loosely inspired by [what-input-js](https://github.com/ten1seven/what-input/blob/main/src/scripts/what-input.js).
 * @internal
 */
export const FocusProvider = ({ children }: WithChildren) => {
  // Ref-counted inertness: nested contain-scopes increment/decrement this so
  // an inner scope unmounting does not clear the inertness an outer scope
  // still relies on.
  const [activeScopeCount, setActiveScopeCount] = useState(0);
  const focusContext = useFocusContext();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Stack of currently active contain-scopes. Stored in a ref to avoid
  // re-rendering on every push/pop. The last entry is the topmost scope.
  const scopeStackRef = useRef<FocusScopeRegistration[]>([]);

  const focusTriggerElement = useCallback((ref: HTMLElement | undefined) => {
    if (!ref) {
      return;
    }

    // The trigger may live outside this provider's inert wrapper (e.g. a
    // button inside a sibling overlay's portal that is itself an active
    // scope). In that case the trigger is reachable regardless of the
    // wrapper's inert state, so focus it immediately.
    if (!wrapperRef.current?.contains(ref)) {
      ref.focus();
      return;
    }

    if (wrapperRef.current.getAttribute('inert') === null) {
      ref.focus();
      return;
    }

    // We want to focus the trigger element after the inert prop got removed.
    // Otherwise, we sometimes lose the focus on slow computers.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'inert'
        ) {
          if (wrapperRef.current?.getAttribute('inert') === null) {
            ref.focus();
            observer.disconnect();
            break;
          }
        }
      }
    });

    observer.observe(wrapperRef.current, { attributes: true });

    // Safety valve: disconnect the observer if the inert attribute is never removed (e.g. abnormal unmount).
    setTimeout(() => {
      observer.disconnect();
    }, 5000);
  }, []);

  const setBackgroundInertness = useCallback((value: boolean) => {
    setActiveScopeCount((count) => Math.max(0, count + (value ? 1 : -1)));
  }, []);

  const registerScope = useCallback((scope: FocusScopeRegistration) => {
    scopeStackRef.current.push(scope);
    return () => {
      const index = scopeStackRef.current.indexOf(scope);
      if (index !== -1) {
        scopeStackRef.current.splice(index, 1);
      }
    };
  }, []);

  const getTopScope = useCallback(() => {
    const stack = scopeStackRef.current;
    return stack.length > 0 ? stack[stack.length - 1] : undefined;
  }, []);

  const isInsideAnyScope = useCallback((element: Element | null) => {
    if (!element) {
      return false;
    }
    return scopeStackRef.current.some((scope) =>
      scope.wrapperRef.current?.contains(element),
    );
  }, []);

  const backgroundInertness = activeScopeCount > 0;

  const focusProps = backgroundInertness
    ? {
        inert: getProperInertValue(true),
        tabIndex: -1,
        'aria-hidden': true,
      }
    : { inert: undefined, tabIndex: undefined, 'aria-hidden': false };

  const contextValue = useMemo(
    () => ({
      ...mergeProps(focusContext, {
        backgroundInertness,
        setBackgroundInertness,
        focusTriggerElement,
        registerScope,
        getTopScope,
        isInsideAnyScope,
      }),
    }),
    [
      focusContext,
      backgroundInertness,
      setBackgroundInertness,
      focusTriggerElement,
      registerScope,
      getTopScope,
      isInsideAnyScope,
    ],
  );

  return (
    <FocusContext.Provider value={contextValue}>
      <div ref={wrapperRef} {...focusProps}>
        {children}
      </div>
    </FocusContext.Provider>
  );
};

FocusProvider['displayName'] = 'FocusProvider';
